import React, { useState } from 'react';
import { Plus, Edit2, Trash2, X, Save, Copy } from 'lucide-react';

const TODO_TYPES = [
  { value: 'info', label: 'Info', description: 'Display information with a "Read" button' },
  { value: 'confirm', label: 'Confirm', description: 'Require client confirmation with custom label' },
  { value: 'upload', label: 'Upload', description: 'File upload with MIME type restrictions' },
  { value: 'link', label: 'Link', description: 'External link with visit confirmation' },
  { value: 'text', label: 'Text', description: 'Multi-line text response from client' }
];

const ClientTodoEditor = ({ todos, clientId, onUpdate, backendUrl, adminKey, isOpen, onClose, editTodo }) => {
  const [formData, setFormData] = useState({
    title: '',
    type: 'info',
    description: '',
    due_date: '',
    required: false,
    accepted_mime: [],
    link_url: '',
    confirm_label: 'I confirm',
    auto_complete_on_upload: false
  });

  // Set form data when editing
  React.useEffect(() => {
    if (editTodo) {
      setFormData({
        title: editTodo.title || '',
        type: editTodo.type || 'info',
        description: editTodo.description || '',
        due_date: editTodo.due_date ? editTodo.due_date.split('T')[0] : '',
        required: editTodo.required || false,
        accepted_mime: editTodo.accepted_mime || [],
        link_url: editTodo.link_url || '',
        confirm_label: editTodo.confirm_label || 'I confirm',
        auto_complete_on_upload: editTodo.auto_complete_on_upload || false
      });
    }
  }, [editTodo]);

  const resetForm = () => {
    setFormData({
      title: '',
      type: 'info',
      description: '',
      due_date: '',
      required: false,
      accepted_mime: [],
      link_url: '',
      confirm_label: 'I confirm',
      auto_complete_on_upload: false
    });
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        due_date: formData.due_date ? `${formData.due_date}T00:00:00Z` : null
      };

      if (editTodo) {
        // Update existing
        const res = await fetch(`${backendUrl}/api/clients/${clientId}/todos/${editTodo.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'x-admin-key': adminKey
          },
          body: JSON.stringify(payload)
        });
        
        if (!res.ok) {
          throw new Error('Failed to update todo');
        }
      } else {
        // Create new
        const res = await fetch(`${backendUrl}/api/clients/${clientId}/todos`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-admin-key': adminKey
          },
          body: JSON.stringify(payload)
        });
        
        if (!res.ok) {
          throw new Error('Failed to create todo');
        }
      }

      resetForm();
      if (onClose) onClose();
      onUpdate();
    } catch (err) {
      console.error('Error saving todo:', err);
      alert('Failed to save to-do: ' + err.message);
    }
  };

  const handleDelete = async (todoId) => {
    if (!confirm('Are you sure you want to delete this to-do?')) return;

    try {
      // Note: We need a delete endpoint, for now we'll update the todos array
      const updatedTodos = todos.filter(t => t.id !== todoId);
      
      await fetch(`${backendUrl}/api/clients/${clientId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey
        },
        body: JSON.stringify({ client_todos: updatedTodos })
      });

      onUpdate();
    } catch (err) {
      console.error('Error deleting todo:', err);
      alert('Failed to delete to-do');
    }
  };

  const handleDuplicate = (todo) => {
    setIsCreating(true);
    setFormData({
      title: `${todo.title} (Copy)`,
      type: todo.type,
      description: todo.description || '',
      due_date: todo.due_date ? todo.due_date.split('T')[0] : '',
      required: todo.required || false,
      accepted_mime: todo.accepted_mime || [],
      link_url: todo.link_url || '',
      confirm_label: todo.confirm_label || 'I confirm'
    });
  };

  const toggleMimeType = (mime) => {
    const current = formData.accepted_mime || [];
    if (current.includes(mime)) {
      setFormData({ ...formData, accepted_mime: current.filter(m => m !== mime) });
    } else {
      setFormData({ ...formData, accepted_mime: [...current, mime] });
    }
  };

  // Modal for creating/editing
  if (isOpen) {
    return (
      <div 
        className="modal-backdrop"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 9998
        }}
        onClick={onClose}
      >
        <div 
          className="modal-shell"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
        >
          <div 
            className="modal-card"
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1E293B' }}>
                {editTodo ? 'Edit To-Do' : 'Create New To-Do'}
              </h3>
              <button 
                type="button"
                onClick={onClose} 
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              >
                <X size={24} color="#64748B" />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Title */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Upload your logo"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Type */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                Type *
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              >
                {TODO_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label} - {type.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Additional details for the client..."
                rows="3"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Type-specific fields */}
            {formData.type === 'upload' && (
              <>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                    Accepted File Types
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {['image/png', 'image/jpeg', 'image/svg+xml', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].map(mime => (
                      <label key={mime} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 12px',
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        background: (formData.accepted_mime || []).includes(mime) ? '#F5F3FF' : '#fff'
                      }}>
                        <input
                          type="checkbox"
                          checked={(formData.accepted_mime || []).includes(mime)}
                          onChange={() => toggleMimeType(mime)}
                        />
                        {mime === 'application/msword' ? 'DOC' : 
                         mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ? 'DOCX' :
                         mime.split('/')[1].toUpperCase()}
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '12px',
                    background: '#F8FAFC',
                    borderRadius: '8px',
                    border: '1px solid #E2E8F0'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.auto_complete_on_upload}
                      onChange={(e) => setFormData({ ...formData, auto_complete_on_upload: e.target.checked })}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                        Auto-complete on upload
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>
                        Automatically mark this task as completed when client uploads a file
                      </div>
                    </div>
                  </label>
                </div>
              </>
            )}

            {formData.type === 'link' && (
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                  Link URL
                </label>
                <input
                  type="url"
                  value={formData.link_url}
                  onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                  placeholder="https://example.com"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
            )}

            {(formData.type === 'confirm' || formData.type === 'info') && (
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                  Button Label
                </label>
                <input
                  type="text"
                  value={formData.confirm_label}
                  onChange={(e) => setFormData({ ...formData, confirm_label: e.target.value })}
                  placeholder="e.g., I have read this"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
            )}

            {/* Due Date */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                Due Date
              </label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Required */}
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.required}
                  onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                />
                <span style={{ fontSize: '14px', color: '#475569' }}>
                  Mark as required
                </span>
              </label>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button
                type="button"
                onClick={handleSave}
                disabled={!formData.title}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '12px',
                  background: formData.title ? '#667eea' : '#CBD5E1',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: formData.title ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'inherit'
                }}
              >
                <Save size={16} />
                {editTodo ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '12px 20px',
                  background: '#F1F5F9',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'inherit'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    );
  }

  // Render nothing when modal is closed
  return null;

};

export default ClientTodoEditor;
